import type { AppDispatch } from '@/store/store';
import type { ThemeColorScheme } from 'src/theme/types';

import {
  canReset,
  isDrawerOpen,
  onCloseDrawer,
  onReset,
  setThemeConfig,
  themeConfigs,
  displayConfigs,
  setDisplayConfig,
} from '@/store/reducerSettings';
import { isUserLogin } from '@/store/reducerUser';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { useColorScheme } from '@mui/material/styles';
import SvgIcon from '@mui/material/SvgIcon';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { hasKeys, varAlpha } from 'minimal-shared/utils';
import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { SettingsDrawerProps } from './types';

import { Iconify } from '../../iconify';
import { Scrollbar } from '../../scrollbar';
import { BaseOption } from './base-option';
import { FontSizeOptions } from './font-options';
import { FullScreenButton } from './fullscreen-button';
import { settingIcons } from './icons';
import { NavColorOptions, NavLayoutOptions } from './nav-layout-option';
import { SmallBlock, LargeBlock } from './styles';

// ----------------------------------------------------------------------

export function SettingsDrawer({
  sx,
  defaultThemeSettings,
  defaultDisplaySettings,
}: SettingsDrawerProps) {
  const dispatch = useDispatch<AppDispatch>();
  const resetEnable = useSelector(canReset);
  const drawerOpen = useSelector(isDrawerOpen);
  const themeConfiguration = useSelector(themeConfigs);
  const displayConfiguration = useSelector(displayConfigs);
  const userLogin = useSelector(isUserLogin);

  const { mode, setMode, systemMode } = useColorScheme();

  useEffect(() => {
    if (mode === 'system' && systemMode) {
      dispatch(setThemeConfig({ colorScheme: systemMode }));
    }
  }, [mode, systemMode]);

  // Visible options by default settings
  const isDirectionVisible = hasKeys(defaultThemeSettings, ['direction']);
  const isColorSchemeVisible = hasKeys(defaultThemeSettings, ['colorScheme']);
  const isContrastVisible = hasKeys(defaultThemeSettings, ['contrast']);

  const handleReset = useCallback(() => {
    dispatch(onReset());
    setMode(defaultThemeSettings.colorScheme as ThemeColorScheme);
  }, [setMode]);

  const renderHead = () => (
    <Box
      sx={{
        py: 2,
        pr: 1,
        pl: 2.5,
        display: 'flex',
        alignItems: 'center',
      }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Settings
      </Typography>

      <FullScreenButton />

      <Tooltip title="Reset all">
        <IconButton onClick={handleReset}>
          <Badge color="error" variant="dot" invisible={!resetEnable}>
            <Iconify icon="solar:restart-bold" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Tooltip title="Close">
        <IconButton onClick={() => dispatch(onReset())}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const renderMode = () => (
    <BaseOption
      label="Dark mode"
      selected={themeConfiguration.colorScheme === 'dark'}
      icon={<SvgIcon>{settingIcons.moon}</SvgIcon>}
      onChangeOption={() => {
        setMode(mode === 'light' ? 'dark' : 'light');
        dispatch(setThemeConfig({ colorScheme: mode === 'light' ? 'dark' : 'light' }));
      }}
    />
  );

  const renderContrast = () => (
    <BaseOption
      label="Contrast"
      selected={themeConfiguration.contrast === 'hight'}
      icon={<SvgIcon>{settingIcons.contrast}</SvgIcon>}
      onChangeOption={() =>
        dispatch(
          setThemeConfig({
            contrast: themeConfiguration.contrast === 'default' ? 'hight' : 'default',
          })
        )
      }
    />
  );

  const renderRtl = () => (
    <BaseOption
      label="Right to left"
      selected={themeConfiguration.direction === 'rtl'}
      icon={<SvgIcon>{settingIcons.alignRight}</SvgIcon>}
      onChangeOption={() =>
        dispatch(
          setThemeConfig({ direction: themeConfiguration.direction === 'ltr' ? 'rtl' : 'ltr' })
        )
      }
    />
  );

  const renderNav = () => (
    <LargeBlock title="Nav" tooltip="Dashboard only" sx={{ gap: 2.5 }}>
      {userLogin && (
        <>
          <SmallBlock
            label="Layout"
            canReset={displayConfiguration.navLayout !== defaultDisplaySettings.navLayout}
            onReset={() =>
              dispatch(setDisplayConfig({ navLayout: defaultDisplaySettings.navLayout }))
            }>
            <NavLayoutOptions
              value={displayConfiguration.navLayout}
              onChangeOption={(newOption) => dispatch(setDisplayConfig({ navLayout: newOption }))}
              options={[
                {
                  value: 'vertical',
                  icon: (
                    <SvgIcon sx={{ width: 1, height: 'auto' }}>{settingIcons.navVertical}</SvgIcon>
                  ),
                },
                {
                  value: 'mini',
                  icon: <SvgIcon sx={{ width: 1, height: 'auto' }}>{settingIcons.navMini}</SvgIcon>,
                },
              ]}
            />
          </SmallBlock>
          <SmallBlock
            label="Color"
            canReset={displayConfiguration.navColor !== defaultDisplaySettings.navColor}
            onReset={() =>
              dispatch(setDisplayConfig({ navColor: defaultDisplaySettings.navColor }))
            }>
            <NavColorOptions
              value={displayConfiguration.navColor}
              onChangeOption={(newOption) => dispatch(setDisplayConfig({ navColor: newOption }))}
              options={[
                {
                  label: 'Integrate',
                  value: 'integrate',
                  icon: <SvgIcon>{settingIcons.sidebarOutline}</SvgIcon>,
                },
                {
                  label: 'Apparent',
                  value: 'apparent',
                  icon: <SvgIcon>{settingIcons.sidebarFill}</SvgIcon>,
                },
              ]}
            />
          </SmallBlock>
        </>
      )}
    </LargeBlock>
  );

  const renderFont = () => (
    <LargeBlock title="Font" sx={{ gap: 2.5 }}>
      <SmallBlock
        label="Size"
        canReset={themeConfiguration.fontSize !== defaultThemeSettings.fontSize}
        onReset={() => dispatch(setThemeConfig({ fontSize: defaultThemeSettings.fontSize }))}
        sx={{ gap: 5 }}>
        <FontSizeOptions
          options={[12, 20]}
          value={themeConfiguration.fontSize}
          onChangeOption={(newOption) => dispatch(setThemeConfig({ fontSize: newOption }))}
        />
      </SmallBlock>
    </LargeBlock>
  );

  return (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={() => dispatch(onCloseDrawer())}
      slotProps={{
        backdrop: { invisible: true },
        paper: {
          sx: [
            (theme) => ({
              ...theme.mixins.paperStyles(theme, {
                color: varAlpha(theme.vars.palette.background.defaultChannel, 0.9),
              }),
              width: 360,
            }),
            ...(Array.isArray(sx) ? sx : [sx]),
          ],
        },
      }}>
      {renderHead()}

      <Scrollbar>
        <Box
          sx={{
            pb: 5,
            gap: 6,
            px: 2.5,
            display: 'flex',
            flexDirection: 'column',
          }}>
          <Box sx={{ gap: 2, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)' }}>
            {isColorSchemeVisible && renderMode()}
            {isContrastVisible && renderContrast()}
            {isDirectionVisible && renderRtl()}
          </Box>

          {renderFont()}
          {userLogin && renderNav()}
        </Box>
      </Scrollbar>
    </Drawer>
  );
}
